// Graphvine portal — reusable UI primitives.
// Designed to be light-DOM, no external deps, easy to drop into any screen.
//
// Visual language sourced from graphvine.ai marketing site:
//   Inter (UI) + JetBrains Mono (data) · ink-900 surfaces · vine-500 accent

import React from 'react';

// ── Vine mark / wordmark ─────────────────────────────────────────────────────
export function VineMark({ size = 28, color = 'var(--g-accent)', className = '' }) {
  return (
    <svg
      width={size * (80 / 110)}
      height={size}
      viewBox="0 0 80 110"
      style={{ display: 'block', color, flexShrink: 0 }}
      className={className}
      aria-hidden="true"
    >
      <g fill="currentColor">
        <ellipse cx="40" cy="12" rx="6" ry="9" />
        <rect x="38" y="18" width="4" height="84" rx="2" />
        <rect x="22" y="30" width="20" height="4" rx="2" />
        <circle cx="22" cy="32" r="4" />
        <rect x="38" y="30" width="20" height="4" rx="2" />
        <circle cx="58" cy="32" r="4" />
        <rect x="14" y="55" width="28" height="4" rx="2" />
        <circle cx="14" cy="57" r="5" />
        <rect x="38" y="55" width="28" height="4" rx="2" />
        <circle cx="66" cy="57" r="5" />
        <circle cx="40" cy="104" r="4" />
      </g>
    </svg>
  );
}

export function Wordmark({ size = 18, color = 'var(--g-text)', accent = 'var(--g-accent)', className = '' }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        fontSize: size,
        color,
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <VineMark size={Math.round(size * 1.4)} color={accent} />
      <span>Graph<span style={{ color: accent }}>v</span>ine</span>
    </span>
  );
}

// ── Animated pulsing dot ─────────────────────────────────────────────────────
export function Dot({ color = 'var(--g-accent-hi)', pulse = false, size = 6 }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', width: size, height: size, flexShrink: 0 }}>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: color }} />
      {pulse && (
        <span
          className="animate-pulsering"
          style={{ position: 'absolute', inset: -3, borderRadius: '50%', background: color }}
        />
      )}
    </span>
  );
}

// ── Pill / chip ──────────────────────────────────────────────────────────────
const PILL_TONES = {
  vine:    { bg: 'var(--g-accent-dim)',    border: 'var(--g-accent-border)', color: 'var(--g-accent-hi)' },
  amber:   { bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.30)',  color: 'var(--g-warn)' },
  danger:  { bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.30)',   color: 'var(--g-danger)' },
  info:    { bg: 'rgba(96,165,250,0.10)',  border: 'rgba(96,165,250,0.30)',  color: 'var(--g-info)' },
  promo:   { bg: 'rgba(251,113,133,0.10)', border: 'rgba(251,113,133,0.30)', color: 'var(--g-promo)' },
  neutral: { bg: 'rgba(148,163,184,0.08)', border: 'var(--g-border-hi)',     color: 'var(--g-text-dim)' },
  // Price-signal tones (token-contract §3) — semantic, reuse brand/status hues.
  better:  { bg: 'var(--g-price-better-bg)', border: 'var(--g-accent-border)', color: 'var(--g-price-better)' },
  worse:   { bg: 'var(--g-price-worse-bg)',  border: 'rgba(239,68,68,0.30)',   color: 'var(--g-price-worse)' },
  sale:    { bg: 'var(--g-sale-bg)',         border: 'rgba(251,113,133,0.30)', color: 'var(--g-sale)' },
};

export function Pill({ children, tone = 'vine', mono = false, className = '', style }) {
  const c = PILL_TONES[tone] || PILL_TONES.vine;
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.01em',
        fontVariantNumeric: 'tabular-nums',
        fontFamily: 'Inter, sans-serif',
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ── Card surface ─────────────────────────────────────────────────────────────
export function Card({ children, accent = false, className = '', style, ...rest }) {
  return (
    <div
      className={className}
      style={{
        background: 'var(--g-card)',
        border: `1px solid ${accent ? 'var(--g-accent-border)' : 'var(--g-border)'}`,
        borderRadius: 10,
        ...(accent ? { boxShadow: 'var(--g-shadow)' } : {}),
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── Mono text (data / timestamps / prices) ───────────────────────────────────
export function Mono({ children, color, size = 12, className = '', style }) {
  return (
    <span
      className={className}
      style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: size,
        color,
        letterSpacing: '0.02em',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

// ── Button ───────────────────────────────────────────────────────────────────
const BTN_SIZES = {
  sm: { padding: '5px 10px',  fontSize: 11 },
  md: { padding: '7px 14px',  fontSize: 12 },
  lg: { padding: '10px 18px', fontSize: 13 },
};

const BTN_VARIANTS = {
  primary: { background: 'var(--g-accent)', color: 'var(--g-on-accent)', border: '1px solid var(--g-accent)', fontWeight: 600 },
  accent:  { background: 'var(--g-accent-dim)', color: 'var(--g-accent-hi)', border: '1px solid var(--g-accent-border)', fontWeight: 600 },
  ghost:   { background: 'transparent', color: 'var(--g-text)', border: '1px solid var(--g-border-hi)', fontWeight: 500 },
  danger:  { background: 'rgba(239,68,68,0.10)', color: 'var(--g-danger)', border: '1px solid rgba(239,68,68,0.30)', fontWeight: 600 },
};

export function Btn({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  onClick,
  disabled,
  type = 'button',
  className = '',
  style,
}) {
  const sStyle = BTN_SIZES[size] || BTN_SIZES.md;
  const vStyle = BTN_VARIANTS[variant] || BTN_VARIANTS.primary;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: 'Inter, sans-serif',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s',
        whiteSpace: 'nowrap',
        ...sStyle,
        ...vStyle,
        ...style,
      }}
    >
      {icon && <span className="material-symbols-outlined" style={{ fontSize: sStyle.fontSize + 3, color: 'inherit' }}>{icon}</span>}
      {children}
    </button>
  );
}

// ── Stat card / KPI tile ─────────────────────────────────────────────────────
export function StatCard({ label, value, sub, subTone = 'vine', children, className = '', style }) {
  const subColors = {
    vine:    'var(--g-accent-hi)',
    amber:   'var(--g-warn)',
    danger:  'var(--g-danger)',
    info:    'var(--g-info)',
    neutral: 'var(--g-text-dim)',
  };
  return (
    <div
      className={className}
      style={{
        background: 'var(--g-card-flat)',
        border: '1px solid var(--g-border)',
        borderRadius: 8,
        padding: '14px 16px',
        flex: 1,
        minWidth: 0,
        ...style,
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: 'var(--g-text-muted)',
          marginBottom: 6,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 24,
          fontWeight: 700,
          color: 'var(--g-text)',
          letterSpacing: '-0.02em',
          lineHeight: 1,
          marginBottom: 6,
          fontFamily: "'JetBrains Mono', monospace",
        }}
      >
        {value}
      </div>
      {sub && (
        <Mono size={10} color={subColors[subTone] || subColors.neutral} style={{ textTransform: 'none' }}>
          {sub}
        </Mono>
      )}
      {children}
    </div>
  );
}

// ── Sparkline (no external lib) ──────────────────────────────────────────────
export function Spark({ data = [], color = 'var(--g-accent-hi)', w = 80, h = 24, fill = true, strokeWidth = 1.5 }) {
  if (!data.length) return <svg width={w} height={h} />;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / Math.max(1, data.length - 1)) * w;
    const y = h - ((v - min) / range) * h;
    return [x, y];
  });
  const path = pts.map(([x, y], i) => (i === 0 ? `M${x},${y}` : `L${x},${y}`)).join(' ');
  const fillPath = `${path} L${w},${h} L0,${h} Z`;
  // Apply color on the <svg> and paint with currentColor so callers can pass a
  // CSS var (var() doesn't resolve in SVG presentation attributes like fill=).
  return (
    <svg width={w} height={h} style={{ display: 'block', color }}>
      {fill && <path d={fillPath} fill="currentColor" opacity={0.15} />}
      <path d={path} fill="none" stroke="currentColor" strokeWidth={strokeWidth} />
    </svg>
  );
}

// ── Grid background (marketing site signature) ───────────────────────────────
export function GridBg({ opacity = 0.5, mask = true, className = '' }) {
  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity,
        backgroundImage:
          'linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)',
        backgroundSize: '56px 56px',
        ...(mask
          ? {
              WebkitMaskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
              maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }
          : {}),
      }}
    />
  );
}

// ── Search input ─────────────────────────────────────────────────────────────
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  width = 280,
  className = '',
  style,
}) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 12px',
        borderRadius: 6,
        width,
        background: 'var(--g-input)',
        border: '1px solid var(--g-border)',
        ...style,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--g-text-faint)' }}>search</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          flex: 1,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: 'var(--g-text)',
          fontSize: 12,
          fontFamily: 'Inter, sans-serif',
          minWidth: 0,
        }}
      />
    </div>
  );
}

// ── Sub-tab bar ──────────────────────────────────────────────────────────────
export function SubTabs({ tabs, active, onChange, className = '' }) {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        gap: 4,
        borderBottom: '1px solid var(--g-border)',
      }}
    >
      {tabs.map(tab => {
        const label = typeof tab === 'string' ? tab : tab.label;
        const isActive = label === active;
        return (
          <button
            key={label}
            onClick={() => onChange && onChange(label)}
            style={{
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? 'var(--g-accent-hi)' : 'var(--g-text-dim)',
              borderBottom: isActive ? '2px solid var(--g-accent)' : '2px solid transparent',
              marginBottom: -1,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              transition: 'color 0.15s',
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

// ── Severity / accent icon tile (used in lists, alerts) ──────────────────────
const TONE_BG = {
  vine:   { bg: 'var(--g-accent-dim)',   color: 'var(--g-accent-hi)' },
  amber:  { bg: 'rgba(245,158,11,0.10)', color: 'var(--g-warn)' },
  danger: { bg: 'rgba(239,68,68,0.10)',  color: 'var(--g-danger)' },
  info:   { bg: 'rgba(96,165,250,0.10)', color: 'var(--g-info)' },
  promo:  { bg: 'rgba(251,113,133,0.10)',color: 'var(--g-promo)' },
  neutral:{ bg: 'rgba(148,163,184,0.08)',color: 'var(--g-text-dim)' },
};

export function IconTile({ icon, tone = 'vine', size = 26, fontSize = 13 }) {
  const c = TONE_BG[tone] || TONE_BG.vine;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 5,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: c.bg,
        color: c.color,
      }}
    >
      <span className="material-symbols-outlined" style={{ fontSize, color: c.color }}>{icon}</span>
    </div>
  );
}

export const TONE_COLORS = {
  vine:   'var(--g-accent-hi)',
  amber:  'var(--g-warn)',
  danger: 'var(--g-danger)',
  info:   'var(--g-info)',
  promo:  'var(--g-promo)',
  neutral:'var(--g-text-dim)',
  better: 'var(--g-price-better)',
  worse:  'var(--g-price-worse)',
  sale:   'var(--g-sale)',
};

// ── Price-signal primitives (token-contract §3 + §4) ─────────────────────────
// Single source of truth for "you're cheaper / pricier / at market" everywhere.
// §4 legibility rule: NEVER color alone — every delta carries an arrow + number,
// so it survives red/green colour-blindness and reads at a glance in dense tables.

// Map a signed delta (yourPrice − marketPrice) → semantic price tone.
//   delta < 0  → you're cheaper  → 'better' (▼)
//   delta > 0  → you're pricier  → 'worse'  (▲)
//   |delta| ≤ epsilon → at market → 'neutral' (–)
export function priceTone(delta, epsilon = 0.005) {
  if (delta == null || Number.isNaN(delta)) return 'neutral';
  if (delta < -epsilon) return 'better';
  if (delta > epsilon) return 'worse';
  return 'neutral';
}

const PRICE_ARROW = { better: '▼', worse: '▲', neutral: '–' };
const PRICE_COLOR = {
  better:  'var(--g-price-better)',
  worse:   'var(--g-price-worse)',
  neutral: 'var(--g-price-neutral)',
};
const PRICE_BG = {
  better:  'var(--g-price-better-bg)',
  worse:   'var(--g-price-worse-bg)',
  neutral: 'var(--g-hover)',
};

// Δ-vs-market pill. Pass either a signed `delta` (auto tone/arrow) or an explicit
// `tone`. `label` overrides the rendered text (else the formatted delta is shown).
export function PriceDelta({
  delta,
  tone,
  label,
  format = (d) => `${d > 0 ? '+' : d < 0 ? '−' : ''}$${Math.abs(d).toFixed(2)}`,
  pill = true,
  size = 11,
  className = '',
  style,
}) {
  const t = tone || priceTone(delta);
  const text = label != null ? label : (delta != null ? format(delta) : '');
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: size,
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: PRICE_COLOR[t],
        whiteSpace: 'nowrap',
        ...(pill
          ? { padding: '2px 8px', borderRadius: 999, background: PRICE_BG[t] }
          : {}),
        ...style,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: size - 1, lineHeight: 1 }}>{PRICE_ARROW[t]}</span>
      {text !== '' && <span>{text}</span>}
    </span>
  );
}

// 5-step price-position indicator collapsed onto the 3-hue scale (§3):
//   Lowest / Below → better · At market → neutral · Above / Highest → worse.
// Extremes are distinguished by label + arrow weight (double arrow), not a new hue.
const PRICE_POSITIONS = {
  lowest:  { tone: 'better',  arrow: '⏬', label: 'Lowest' },
  below:   { tone: 'better',  arrow: '▼', label: 'Below avg' },
  at:      { tone: 'neutral', arrow: '–', label: 'At market' },
  above:   { tone: 'worse',   arrow: '▲', label: 'Above avg' },
  highest: { tone: 'worse',   arrow: '⏫', label: 'Highest' },
};

export function PricePosition({ position, label, size = 11, className = '', style }) {
  const p = PRICE_POSITIONS[position] || PRICE_POSITIONS.at;
  const tone = p.tone;
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: '0.01em',
        fontFamily: 'Inter, sans-serif',
        color: PRICE_COLOR[tone],
        background: PRICE_BG[tone],
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span aria-hidden="true" style={{ lineHeight: 1 }}>{p.arrow}</span>
      <span>{label != null ? label : p.label}</span>
    </span>
  );
}

// On-sale / promo tag.
export function SaleTag({ children = 'Sale', icon = 'sell', size = 11, className = '', style }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: size,
        fontWeight: 600,
        letterSpacing: '0.01em',
        fontFamily: 'Inter, sans-serif',
        color: 'var(--g-sale)',
        background: 'var(--g-sale-bg)',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {icon && <span className="material-symbols-outlined" style={{ fontSize: size + 3, color: 'inherit' }}>{icon}</span>}
      {children}
    </span>
  );
}
